import moment from "moment";
import { AN_COLORS } from "../design-system/tokens";

// Decision rendering metadata for the auth-log table, drawer and dashboard widget.
// Keep `color` aligned with the design-system tokens so it matches the rest of the app.
export const DECISION_META = {
  allow: { label: "Allow", color: AN_COLORS.success, tone: "success" },
  deny: { label: "Deny", color: AN_COLORS.danger, tone: "danger" },
  mfa_required: { label: "MFA required", color: AN_COLORS.warn, tone: "warn" },
};

export const decisionMeta = (decision) =>
  DECISION_META[decision] || { label: decision || "—", color: AN_COLORS.muted, tone: "neutral" };

// Kerberos / KDC upstream error codes → human-readable names.
// The GetAuthBadSources endpoint already returns `lastErrorName`; this map is used by the
// log table where only the numeric `upstreamErrorCode` is present.
// Codes 14 (KDC_ERR_ETYPE_NOSUPP) and 25 (KDC_ERR_PREAUTH_REQUIRED) are treated as benign noise.
export const KRB_ERROR_NAMES = {
  0: "NONE",
  6: "C_PRINCIPAL_UNKNOWN",
  7: "S_PRINCIPAL_UNKNOWN",
  12: "POLICY",
  14: "ETYPE_NOSUPP",
  18: "CLIENT_REVOKED",
  23: "KEY_EXPIRED",
  24: "PREAUTH_FAILED",
  25: "PREAUTH_REQUIRED",
  37: "SKEW",
};

export const BENIGN_KRB_CODES = new Set([14, 25]);

export const errorName = (code) => {
  if (code === undefined || code === null || code === "") return "";
  const num = Number(code);
  if (num === 0) return "";
  return KRB_ERROR_NAMES[num] || `Code ${num}`;
};

// Normalize the `[{ tsUnixMs, count }]` timeline into rows for the @ant-design/plots Column chart.
// `fmt` controls the bucket label format (15-min buckets by default).
export const bucketTimeline = (timeline, fmt = "MMM D HH:mm") =>
  (timeline || []).map((b) => ({
    label: moment(b.tsUnixMs).format(fmt),
    tsUnixMs: b.tsUnixMs,
    count: Number(b.count) || 0,
  }));

// Aggregate the server's 15-min timeline buckets into per-day buckets, with a continuous
// zero-filled day axis (like the platform dashboard chart). `maxDays` guards against generating
// thousands of bars for very wide all-time ranges — beyond it, only populated days are plotted.
export const dailyTimeline = (timeline, maxDays = 120) => {
  if (!timeline || !timeline.length) return [];
  const byDay = new Map();
  timeline.forEach((b) => {
    const day = moment(b.tsUnixMs).startOf("day").valueOf();
    byDay.set(day, (byDay.get(day) || 0) + (Number(b.count) || 0));
  });
  const days = [...byDay.keys()].sort((a, b) => a - b);
  const first = days[0];
  const last = days[days.length - 1];
  const span = moment(last).diff(moment(first), "days") + 1;
  const rows = [];
  if (span <= maxDays) {
    for (let m = moment(first); m.valueOf() <= last; m.add(1, "day")) {
      const d = m.valueOf();
      rows.push({ label: moment(d).format("MMM D"), tsUnixMs: d, count: byDay.get(d) || 0 });
    }
  } else {
    days.forEach((d) =>
      rows.push({ label: moment(d).format("MMM D"), tsUnixMs: d, count: byDay.get(d) })
    );
  }
  return rows;
};

// Count events by decision — used by the dashboard quick view and drawer summaries.
export const countByDecision = (events) => {
  const counts = { allow: 0, deny: 0, mfa_required: 0, total: 0 };
  (events || []).forEach((e) => {
    counts.total += 1;
    if (counts[e.decision] !== undefined) counts[e.decision] += 1;
  });
  return counts;
};
